import { PublicationError } from '@streamlain/doc-publication-contract';
import { localAccountKeyError } from '@streamlain/view-config/local-data-seal';

import {
  decodeLocalIndexBlock,
  decodeLocalIndexHistory,
  decodeLocalIndexRecord,
  decodeLocalIndexRunPart,
  docKeyScope,
  encodeLocalIndexBlock,
  encodeLocalIndexHistory,
  encodeLocalIndexRecord,
  encodeLocalIndexRunPart,
  isPreviousLocalIndexHistory,
  type LocalIndexBlockIdentity,
  type LocalIndexBlockRef,
  localIndexBlockScope,
  type LocalIndexHistory,
  type LocalIndexPosting,
  type LocalIndexRow,
  type LocalIndexRunPart,
  localIndexRunPartId,
  localIndexScope,
  type LocalIndexSegment,
  type LocalIndexSegmentIdentity,
  MAX_LOCAL_INDEX_BATCH_BYTES,
  MAX_LOCAL_INDEX_BATCH_RECORDS,
} from '../../../local-data/payload';
import {
  AccountSearchIndexSource,
  searchIndexFailure,
  type SearchIndexLease,
} from '../../../local-data/search-index-source';
import { type IndexerSchema, isIndexerTable } from '../../../storage/indexer';
import type {
  IndexDocumentInput,
  IndexQueryInput,
  IndexReadTransaction,
  IndexRecord,
} from './access';
import type { DataStruct } from './data-struct';
import {
  SealedIndex,
  type SealedIndexLoader,
  type SealedIndexTransaction,
} from './sealed-access';
import {
  type IndexSegmentCAS,
  type IndexSegmentHead,
  type IndexSegmentKeys,
  type IndexSegmentStart,
  type IndexSegmentStore,
  sameIndexHead,
} from './segment-store';

export interface IndexWriteBatch {
  deleteByQueries: IndexQueryInput[];
  deletes: string[];
  inserts: IndexDocumentInput[];
  updates: IndexDocumentInput[];
}
/** Plaintext target of one postings block. */
export const INDEX_BLOCK_BYTES = 128 * 1024;
/** Plaintext one merge step writes before it commits and lets reads in. */
const MERGE_STEP_BYTES = 4 * 1024 * 1024;
/** More runs than this are merged even when the sizes do not ask for it. */
const MAX_INDEX_RUNS = 16;
/** Bytes a sealed row adds to its plaintext: IV and tag. */
const SEAL_OVERHEAD = 28;
const recordKey = (table: string, id: string) => JSON.stringify([table, id]);
const cipherBytes = (row: LocalIndexRow) =>
  row.payload.sealed.ciphertext.byteLength + row.payload.sealed.iv.byteLength;
const runPostings = (run: readonly LocalIndexBlockRef[]) =>
  run.reduce((sum, block) => sum + block.count, 0);
/** Plaintext estimate of one posting inside a block. */
const postingBytes = (posting: LocalIndexPosting) =>
  posting[1].length + 24 + (posting[3]?.length ?? 0) * 4;
class IndexBatchTooLarge extends Error {
  constructor() {
    super('LOCAL_QUOTA');
  }
}

type Identity = LocalIndexSegmentIdentity | LocalIndexBlockIdentity;
interface PlainRow {
  identity: Identity;
  bytes: Uint8Array;
  /** Stored revision this put replaces; null for a new row. */
  expected: string | null;
  /** Sealed bytes of the row it replaces. */
  replacedBytes: number;
}
interface StoredDelete extends IndexSegmentCAS {
  bytes: number;
}
interface CommitStep {
  puts: PlainRow[];
  deletes: StoredDelete[];
  /** A run part listing blocks of `puts`, sealed once their sizes are known. */
  part?: { segmentId: string; prev: string | null; blocks: PendingBlock[] };
  /** The head metadata after this commit, from the new rows' sealed sizes. */
  history(
    sealedBytes: number,
    rowBytes: Map<string, number>
  ): LocalIndexHistory;
}
interface PendingBlock {
  ref: Omit<LocalIndexBlockRef, 'bytes'>;
  row: PlainRow;
}
/** A stored run part with the sealed bytes of its row. */
interface StoredPart extends LocalIndexRunPart {
  bytes: number;
}
/** A read waiting in a read group. */
interface GroupRead {
  lease: SearchIndexLease;
  work: (tx: IndexReadTransaction) => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}
/** Reads that run under one pair of authority checks. */
interface ReadGroup {
  reads: GroupRead[];
}
/** One input run of a merge, read block by block. */
interface MergeCursor {
  blocks: readonly LocalIndexBlockRef[];
  next: number;
  postings: LocalIndexPosting[];
  at: number;
}
interface Merge {
  inputs: LocalIndexBlockRef[][];
  cursors: MergeCursor[];
  output: LocalIndexBlockRef[];
  /** Last part of the merged run written so far. */
  chain: string | null;
  /** A full merge drops every removed record counted when it started. */
  deadAtStart: number | null;
}

/**
 * The account search index as sealed rows on disk: one row per record, runs
 * of sealed postings blocks, run parts listing those blocks and one head
 * metadata row naming the last part of each run. Start reads the head, the
 * run parts and the record indexes; searches decrypt the records and blocks
 * they touch. Each flush commits its records, a new run of their postings
 * and the head together; merges and deletion of replaced runs run as later
 * serialized steps.
 */
export class EncryptedIndexSegments<
  K extends IndexSegmentKeys = IndexSegmentKeys,
> {
  readonly source: AccountSearchIndexSource;
  private index: SealedIndex;
  /** Last part of each run of `index.runs`, in the same order. */
  private runTails: string[] = [];
  /** Last part of each chain that belongs to no run yet. */
  private pending: string[] = [];
  /** Last part of each chain of replaced runs, still to delete. */
  private garbage: string[] = [];
  /** Sealed bytes of every row except the head metadata row. */
  private sealedBytes = 0;
  private metaBytes = 0;
  private deadRecords = 0;
  private merge: Merge | null = null;
  private revision: string | null = null;
  private metaId: string | null = null;
  private authorityContext: K | null = null;
  /** Abort signal of the serialized work that is running. */
  private signal: AbortSignal | null = null;
  private tail: Promise<void> = Promise.resolve();
  /** The read group queued last, open for more reads until it starts. */
  private group: ReadGroup | null = null;
  private maintenance: Promise<void> = Promise.resolve();
  private readonly loader: SealedIndexLoader = {
    records: nids => this.loadRecords(nids),
    blocks: refs => this.loadBlocks(refs),
    nids: async keys =>
      (await this.store.readRoutes(this.head, keys, this.current())).map(
        row => row?.nid
      ),
  };
  constructor(
    private readonly store: IndexSegmentStore<K>,
    private readonly data: DataStruct
  ) {
    this.index = new SealedIndex(this.loader);
    this.source = new AccountSearchIndexSource(
      store.binding,
      signal => this.rebuild(signal),
      () => this.serial(() => this.assertAuthority()),
      () => store.authorized,
      () => {
        this.index = new SealedIndex(this.loader);
        this.runTails = [];
        this.pending = [];
        this.garbage = [];
        this.sealedBytes = 0;
        this.metaBytes = 0;
        this.deadRecords = 0;
        this.merge = null;
        this.revision = null;
        this.metaId = null;
        this.authorityContext = null;
      }
    );
    const off = store.onClosed(() => {
      this.source.invalidate(localAccountKeyError('LOCKED'));
      off();
    });
  }
  private get workspaceId() {
    return this.source.binding.workspaceId;
  }
  private get head() {
    return this.revision === null || this.metaId === null
      ? null
      : { revision: this.revision, segmentId: this.metaId };
  }
  private current() {
    if (!this.signal) throw localAccountKeyError('LOCKED');
    return this.signal;
  }
  private serial<T>(work: () => Promise<T>): Promise<T> {
    const result = this.tail.then(work);
    this.tail = result.then(
      () => {},
      () => {}
    );
    return result;
  }
  /**
   * Work between two authority checks under a current lease; a failure
   * invalidates the index. Runs inside the serial order.
   */
  private async guarded<T>(
    lease: SearchIndexLease,
    work: (signal: AbortSignal) => Promise<T>
  ): Promise<T> {
    try {
      this.source.assertCurrent(lease);
      await this.assertAuthority();
      this.signal = lease.signal;
      try {
        const result = await work(lease.signal);
        await this.assertAuthority();
        this.source.assertCurrent(lease);
        return result;
      } finally {
        this.signal = null;
      }
    } catch (error) {
      const failure = lease.signal.aborted ? lease.signal.reason : error;
      this.source.invalidate(failure);
      throw searchIndexFailure(failure);
    }
  }
  /** Serialized writing work: alone, after every read and write before it. */
  private async leased<T>(
    work: (signal: AbortSignal) => Promise<T>
  ): Promise<T> {
    // A read after this write waits for it.
    this.group = null;
    const lease = await this.source.awaitReady();
    return this.serial(() => this.guarded(lease, work));
  }
  private async assertAuthority() {
    if (!this.authorityContext) throw localAccountKeyError('LOCKED');
    await this.store.assertSnapshot(this.authorityContext, this.head);
  }
  /**
   * A read joins the group queued last, if it has not started: the reads of
   * a group share one authority check before and one after their work, and
   * none returns before the check after.
   */
  async read<T>(work: (tx: IndexReadTransaction) => Promise<T>): Promise<T> {
    const lease = await this.source.awaitReady();
    return new Promise<T>((resolve, reject) => {
      let group = this.group;
      if (!group) {
        group = { reads: [] };
        this.group = group;
        void this.serial(() => this.runGroup(group!));
      }
      group.reads.push({
        lease,
        work,
        resolve: resolve as (value: unknown) => void,
        reject,
      });
    });
  }
  private async runGroup(group: ReadGroup) {
    // A read whose lease is stale fails alone.
    const current = (read: GroupRead) => {
      try {
        this.source.assertCurrent(read.lease);
        return true;
      } catch (error) {
        read.reject(searchIndexFailure(error));
        return false;
      }
    };
    const first = group.reads.find(current);
    if (!first) {
      if (this.group === group) this.group = null;
      return;
    }
    try {
      const results = await this.guarded(first.lease, async () => {
        // Reads join until the check before is done.
        if (this.group === group) this.group = null;
        const values = new Map<GroupRead, unknown>();
        for (const read of group.reads)
          if (read === first || current(read))
            values.set(read, await read.work(this.index.readonly()));
        return values;
      });
      for (const [read, value] of results) read.resolve(value);
    } catch (error) {
      // A failed check before closes the group as well.
      if (this.group === group) this.group = null;
      for (const read of group.reads) read.reject(error);
    }
  }
  private validateRow(row: LocalIndexRow | undefined) {
    if (!row || !row.payload || row.payload.keyScopeId !== row.keyScopeId)
      throw localAccountKeyError('CORRUPT');
    if (row.role === 'postings' || row.role === 'run') {
      if (
        row.ownerDocId !== this.workspaceId ||
        row.keyScopeId !== docKeyScope(this.workspaceId) ||
        row.storageRevision !== row.segmentId ||
        'nid' in row ||
        'recordKey' in row
      )
        throw localAccountKeyError('CORRUPT');
    } else if (
      row.keyScopeId !== docKeyScope(row.ownerDocId) ||
      (row.role === 'record'
        ? !row.recordKey ||
          row.recordKey[0] !== row.table ||
          row.recordKey[1] !== row.recordId
        : row.role !== 'metadata' ||
          row.recordKey !== undefined ||
          row.nid !== 0)
    )
      throw localAccountKeyError('CORRUPT');
    if (cipherBytes(row) > MAX_LOCAL_INDEX_BATCH_BYTES)
      throw new PublicationError('LOCAL_QUOTA');
  }
  private scopeOf(identity: Identity) {
    return identity.role === 'record' || identity.role === 'metadata'
      ? localIndexScope(identity.ownerDocId, identity.table, identity.recordId)
      : localIndexBlockScope(identity.ownerDocId, identity.segmentId);
  }
  private async open(context: K, row: LocalIndexRow) {
    this.validateRow(row);
    return context.open(
      row.keyScopeId,
      this.scopeOf(row),
      'index',
      row.payload
    );
  }
  private validateStart(start: IndexSegmentStart) {
    const { state, meta, count } = start;
    if (
      state &&
      (!meta ||
        meta.role !== 'metadata' ||
        meta.storageRevision !== state.revision ||
        meta.ownerDocId !== this.workspaceId ||
        meta.table !== 'doc' ||
        meta.recordId !== meta.segmentId)
    )
      throw localAccountKeyError('CORRUPT');
    if (!state && count) throw localAccountKeyError('CORRUPT');
  }
  private async loadRecords(nids: readonly number[]) {
    const read = await this.store.readRows(this.head, nids, this.current());
    try {
      const records = new Map<number, IndexRecord>();
      for (const [at, row] of read.value.entries()) {
        if (!row || row.role !== 'record' || row.nid !== nids[at])
          throw localAccountKeyError('CORRUPT');
        const plain = await this.open(read.context, row);
        try {
          records.set(row.nid, {
            table: row.table,
            id: row.recordId,
            data: decodeLocalIndexRecord(plain, row),
          });
        } finally {
          plain.fill(0);
        }
      }
      return records;
    } finally {
      read.context.releasePreparedKeys();
    }
  }
  private async loadBlocks(refs: readonly LocalIndexBlockRef[]) {
    const read = await this.store.readRows(
      this.head,
      refs.map(ref => ref.segmentId),
      this.current()
    );
    try {
      const blocks = new Map<string, LocalIndexPosting[]>();
      for (const [at, row] of read.value.entries()) {
        const ref = refs[at];
        if (!row || row.role !== 'postings' || row.segmentId !== ref.segmentId)
          throw localAccountKeyError('CORRUPT');
        const plain = await this.open(read.context, row);
        try {
          const postings = decodeLocalIndexBlock(plain, row);
          if (
            postings.length !== ref.count ||
            postings[0][1] !== ref.first ||
            postings[postings.length - 1][1] !== ref.last
          )
            throw localAccountKeyError('CORRUPT');
          blocks.set(ref.segmentId, postings);
        } finally {
          plain.fill(0);
        }
      }
      return blocks;
    } finally {
      read.context.releasePreparedKeys();
    }
  }
  private async loadParts(
    expected: IndexSegmentHead | null,
    ids: readonly string[],
    signal: AbortSignal
  ) {
    const read = await this.store.readRows(expected, ids, signal);
    try {
      const parts: StoredPart[] = [];
      for (const [at, row] of read.value.entries()) {
        if (!row || row.role !== 'run' || row.segmentId !== ids[at])
          throw localAccountKeyError('CORRUPT');
        const plain = await this.open(read.context, row);
        try {
          parts.push({
            ...decodeLocalIndexRunPart(plain, row),
            bytes: cipherBytes(row),
          });
        } finally {
          plain.fill(0);
        }
      }
      return parts;
    } finally {
      read.context.releasePreparedKeys();
    }
  }
  /** Every stored run part by id, read in one transaction. */
  private async readAllParts(
    expected: IndexSegmentHead | null,
    signal: AbortSignal
  ) {
    const read = await this.store.readRunParts(expected, signal);
    try {
      const parts = new Map<string, StoredPart>();
      for (const row of read.value) {
        if (row.role !== 'run') throw localAccountKeyError('CORRUPT');
        const plain = await this.open(read.context, row);
        try {
          parts.set(row.segmentId, {
            ...decodeLocalIndexRunPart(plain, row),
            bytes: cipherBytes(row),
          });
        } finally {
          plain.fill(0);
        }
      }
      return parts;
    } finally {
      read.context.releasePreparedKeys();
    }
  }
  /**
   * Blocks of each chain, oldest part first, walked back from the last part.
   * `seen` collects every part and block id.
   */
  private chains(
    parts: ReadonlyMap<string, StoredPart>,
    tails: readonly string[],
    seen: Set<string>
  ) {
    return tails.map(tail => {
      const chain: StoredPart[] = [];
      for (let id: string | null = tail; id; ) {
        const part = parts.get(id);
        if (!part || seen.has(id)) throw localAccountKeyError('CORRUPT');
        seen.add(id);
        for (const block of part.blocks) {
          if (seen.has(block.segmentId)) throw localAccountKeyError('CORRUPT');
          seen.add(block.segmentId);
        }
        chain.unshift(part);
        id = part.prev;
      }
      const blocks = chain.flatMap(part => part.blocks);
      for (let at = 1; at < blocks.length; at++)
        if (blocks[at].first < blocks[at - 1].last)
          throw localAccountKeyError('CORRUPT');
      return blocks;
    });
  }
  private async rebuild(signal: AbortSignal): Promise<void> {
    await this.store.waitForConnected(signal);
    const store = this.store;
    const start = await store.readStart(
      value => this.validateStart(value),
      signal
    );
    let history: LocalIndexHistory | null = null;
    let previous = false;
    if (start.value.meta) {
      const plain = await this.open(start.context, start.value.meta);
      try {
        previous = isPreviousLocalIndexHistory(plain, start.value.meta);
        if (!previous)
          history = decodeLocalIndexHistory(plain, start.value.meta);
      } finally {
        plain.fill(0);
      }
    }
    await store.assertRead(start.context);
    if (previous) {
      // An index of the previous layout is derived data: drop it once and
      // start empty; the indexer indexes every doc again (INDEXER_VERSION).
      start.context.releasePreparedKeys();
      await store.discard(start.value.state, signal);
      return await this.rebuild(signal);
    }
    const live = history
      ? await store.readRecordTables(start.value.state, signal)
      : new Map<number, string>();
    let runs: LocalIndexBlockRef[][] = [];
    if (history) {
      // Every part and block of a run, a pending chain or a replaced run.
      const seen = new Set<string>();
      const parts = await this.readAllParts(start.value.state, signal);
      runs = this.chains(parts, history.runs, seen);
      this.chains(parts, [...history.pending, ...history.garbage], seen);
      let maxNid = 0;
      for (const [nid, table] of live) {
        if (!isIndexerTable(table)) throw localAccountKeyError('CORRUPT');
        maxNid = Math.max(maxNid, nid);
      }
      if (
        live.size !== history.recordCount ||
        runs.some(run => !run.length) ||
        start.value.count !== live.size + 1 + seen.size ||
        maxNid >= history.nextNid
      )
        throw localAccountKeyError('CORRUPT');
    }
    const end = await store.readSnapshot(signal);
    if (
      !sameIndexHead(end.state, start.value.state) ||
      end.count !== start.value.count
    )
      throw new PublicationError('LOCAL_CONFLICT');
    await store.assertRead(start.context);
    if (signal.aborted) throw localAccountKeyError('LOCKED');
    const index = new SealedIndex(this.loader);
    index.setLive(live);
    if (history) {
      for (const [key, value] of history.metadata)
        index.metadata.set(key, { key, value });
      index.runs = runs;
      index.nextNid = history.nextNid;
      index.nextPosting = history.nextPosting;
    }
    this.index = index;
    this.runTails = history?.runs ?? [];
    this.pending = [];
    // Chains of a merge that did not finish belong to no run: delete them.
    this.garbage = history ? [...history.garbage, ...history.pending] : [];
    this.sealedBytes = history?.sealedBytes ?? 0;
    this.deadRecords = history?.deadRecords ?? 0;
    this.merge = null;
    this.metaBytes = start.value.meta ? cipherBytes(start.value.meta) : 0;
    this.revision = start.value.state?.revision ?? null;
    this.metaId = start.value.state?.segmentId ?? null;
    start.context.releasePreparedKeys();
    this.authorityContext = start.context;
    this.scheduleMaintenance();
  }
  async flush(table: keyof IndexerSchema, batch: IndexWriteBatch) {
    await this.leased(async signal => {
      // The matches of all queries share one route read: a crawl batch
      // carries one query per page.
      const nids = new Set<number>();
      for (const query of batch.deleteByQueries) {
        const match = await this.data.queryRaw(
          this.index.readonly(),
          table,
          query
        );
        for (const nid of match.scores.keys()) nids.add(nid);
      }
      if (nids.size > 0) {
        const routes = await this.store.readRoutes(
          this.head,
          [...nids],
          signal
        );
        const deletes = routes.map(route => {
          if (!route) throw localAccountKeyError('CORRUPT');
          return route.recordId;
        });
        await this.chunks(table, deletes, [], [], signal);
      }
      await this.chunks(table, batch.deletes, [], [], signal);
      await this.chunks(table, [], batch.inserts, [], signal);
      await this.chunks(table, [], [], batch.updates, signal);
    });
    this.scheduleMaintenance();
  }
  private async chunks(
    table: keyof IndexerSchema,
    deletes: string[],
    inserts: IndexDocumentInput[],
    updates: IndexDocumentInput[],
    signal: AbortSignal
  ) {
    const size = Math.max(deletes.length, inserts.length, updates.length);
    for (let i = 0; i < size; i += MAX_LOCAL_INDEX_BATCH_RECORDS - 1)
      await this.commitBatch(
        table,
        deletes.slice(i, i + MAX_LOCAL_INDEX_BATCH_RECORDS - 1),
        inserts.slice(i, i + MAX_LOCAL_INDEX_BATCH_RECORDS - 1),
        updates.slice(i, i + MAX_LOCAL_INDEX_BATCH_RECORDS - 1),
        signal
      );
  }
  private async commitBatch(
    table: keyof IndexerSchema,
    deletes: string[],
    inserts: IndexDocumentInput[],
    updates: IndexDocumentInput[],
    signal: AbortSignal
  ): Promise<void> {
    const keys = [
      ...new Set([
        ...deletes,
        ...inserts.map(document => document.id),
        ...updates.map(document => document.id),
      ]),
    ].map((id): [string, string] => [table, id]);
    const stored = await this.store.readRoutes(this.head, keys, signal);
    const routes = new Map<string, LocalIndexSegment>();
    const known = new Map<string, number | null>();
    for (const [at, [, id]] of keys.entries()) {
      const route = stored[at];
      if (route) routes.set(recordKey(table, id), route);
      known.set(recordKey(table, id), route?.nid ?? null);
    }
    const overlay = this.index.readwrite(known);
    await this.data.batchWrite(overlay, table, [], deletes, inserts, updates);
    try {
      await this.persist(overlay, routes, signal);
    } catch (error) {
      const size = Math.max(deletes.length, inserts.length, updates.length);
      if (!(error instanceof IndexBatchTooLarge)) throw error;
      if (size <= 1) throw new PublicationError('LOCAL_QUOTA');
      const half = Math.ceil(size / 2);
      await this.commitBatch(
        table,
        deletes.slice(0, half),
        inserts.slice(0, half),
        updates.slice(0, half),
        signal
      );
      await this.commitBatch(
        table,
        deletes.slice(half),
        inserts.slice(half),
        updates.slice(half),
        signal
      );
    }
  }
  private recordRows(
    overlay: SealedIndexTransaction,
    routes: Map<string, LocalIndexSegment>
  ) {
    const byNid = new Map<number, LocalIndexSegment>();
    for (const route of routes.values()) byNid.set(route.nid, route);
    const changes = new Map<
      string,
      { nid: number; data: IndexRecord | null }
    >();
    let dead = 0;
    for (const [nid, data] of overlay.records) {
      if (data === null && this.index.live.has(nid)) dead++;
      const route = byNid.get(nid);
      if (data) changes.set(recordKey(data.table, data.id), { nid, data });
      else if (route)
        changes.set(recordKey(route.table, route.recordId), { nid, data });
    }
    const puts: PlainRow[] = [];
    const deletes: StoredDelete[] = [];
    try {
      for (const [key, { nid, data }] of changes) {
        const old = routes.get(key);
        if (!data) {
          if (old)
            deletes.push({
              segmentId: old.segmentId,
              expected: old.storageRevision,
              bytes: cipherBytes(old),
            });
          continue;
        }
        if (!isIndexerTable(data.table)) throw localAccountKeyError('CORRUPT');
        const docIds = data.data.get('docId');
        if (!docIds || docIds.length !== 1 || !docIds[0])
          throw localAccountKeyError('CORRUPT');
        const identity: LocalIndexSegmentIdentity = {
          segmentId: old?.segmentId ?? crypto.randomUUID(),
          role: 'record',
          table: data.table,
          recordId: data.id,
          ownerDocId: docIds[0],
          nid,
          storageRevision: crypto.randomUUID(),
        };
        puts.push({
          identity,
          bytes: encodeLocalIndexRecord(identity, data.data),
          expected: old?.storageRevision ?? null,
          replacedBytes: old ? cipherBytes(old) : 0,
        });
      }
    } catch (error) {
      for (const row of puts) row.bytes.fill(0);
      throw error;
    }
    return { puts, deletes, dead };
  }
  /** Sealed postings blocks of sorted postings, about INDEX_BLOCK_BYTES each. */
  private blocks(postings: readonly LocalIndexPosting[]): PendingBlock[] {
    const blocks: PendingBlock[] = [];
    let start = 0;
    let bytes = 0;
    const cut = (end: number) => {
      const part = postings.slice(start, end);
      const segmentId = crypto.randomUUID();
      const identity: LocalIndexBlockIdentity = {
        segmentId,
        role: 'postings',
        ownerDocId: this.workspaceId,
        storageRevision: segmentId,
      };
      blocks.push({
        ref: {
          segmentId,
          first: part[0][1],
          last: part[part.length - 1][1],
          count: part.length,
        },
        row: {
          identity,
          bytes: encodeLocalIndexBlock(identity, part),
          expected: null,
          replacedBytes: 0,
        },
      });
      start = end;
      bytes = 0;
    };
    try {
      for (let at = 0; at < postings.length; at++) {
        bytes += postingBytes(postings[at]);
        if (bytes >= INDEX_BLOCK_BYTES) cut(at + 1);
      }
      if (start < postings.length) cut(postings.length);
    } catch (error) {
      for (const block of blocks) block.row.bytes.fill(0);
      throw error;
    }
    return blocks;
  }
  private history(
    overrides: Partial<LocalIndexHistory> & { sealedBytes: number }
  ): LocalIndexHistory {
    return {
      nextNid: this.index.nextNid,
      nextPosting: this.index.nextPosting,
      recordCount: this.index.live.size,
      metadata: [...this.index.metadata.values()].map(entry => [
        entry.key,
        entry.value,
      ]),
      runs: this.runTails,
      pending: this.pending,
      garbage: this.garbage,
      deadRecords: this.deadRecords,
      ...overrides,
    };
  }
  private async persist(
    overlay: SealedIndexTransaction,
    routes: Map<string, LocalIndexSegment>,
    signal: AbortSignal
  ) {
    const records = this.recordRows(overlay, routes);
    let blocks: PendingBlock[] = [];
    try {
      const recordBytes = records.puts.reduce(
        (sum, row) => sum + row.bytes.length + SEAL_OVERHEAD,
        0
      );
      // The records and the head must fit one commit; else halve the batch.
      if (
        records.puts.length + records.deletes.length + 1 >
          MAX_LOCAL_INDEX_BATCH_RECORDS ||
        recordBytes + INDEX_BLOCK_BYTES > MAX_LOCAL_INDEX_BATCH_BYTES
      )
        throw new IndexBatchTooLarge();
      blocks = this.blocks(overlay.storedPostings());
      // Blocks beyond the final commit are stored first as a pending chain.
      // The final commit holds the records, its blocks, their part and the head.
      let rows = records.puts.length + records.deletes.length + 2;
      let bytes = recordBytes;
      let split = 0;
      for (const block of blocks) {
        const size = block.row.bytes.length + SEAL_OVERHEAD;
        rows++;
        bytes += size;
        if (
          rows > MAX_LOCAL_INDEX_BATCH_RECORDS ||
          bytes > MAX_LOCAL_INDEX_BATCH_BYTES
        )
          split = blocks.indexOf(block) + 1;
      }
      const run: LocalIndexBlockRef[] = [];
      let chain: string | null = null;
      for (const part of this.commitParts(blocks.slice(0, split))) {
        const stored = await this.commitPending(part, chain, signal);
        run.push(...stored.blocks);
        chain = stored.tail;
      }
      const last = blocks.slice(split);
      const tail = last.length ? localIndexRunPartId() : chain;
      const dead = records.dead;
      let runs = this.index.runs;
      await this.commit(
        {
          puts: [...records.puts, ...last.map(block => block.row)],
          deletes: records.deletes,
          part:
            tail && last.length
              ? { segmentId: tail, prev: chain, blocks: last }
              : undefined,
          history: (sealedBytes, rowBytes) => {
            const added = last.map(block => ({
              ...block.ref,
              bytes: rowBytes.get(block.ref.segmentId) ?? 0,
            }));
            runs = tail ? [...this.index.runs, [...run, ...added]] : runs;
            return this.history({
              sealedBytes,
              nextNid: overlay.nextNid,
              nextPosting: overlay.nextPosting,
              recordCount: overlay.recordCount(),
              metadata: [...overlay.metadata.entries()].map(([key, value]) => [
                key,
                value.value,
              ]),
              runs: tail ? [...this.runTails, tail] : this.runTails,
              pending: this.pending.filter(part => part !== chain),
              deadRecords: this.deadRecords + dead,
            });
          },
        },
        signal,
        history => {
          overlay.publish();
          this.index.runs = runs;
          this.runTails = history.runs;
          this.pending = history.pending;
          this.deadRecords = history.deadRecords;
        }
      );
    } finally {
      for (const row of records.puts) row.bytes.fill(0);
      for (const block of blocks) block.row.bytes.fill(0);
    }
  }
  /** Blocks cut into parts that each fit one commit with their part and the head. */
  private *commitParts(blocks: readonly PendingBlock[]) {
    let part: PendingBlock[] = [];
    let bytes = 0;
    for (const block of blocks) {
      const size = block.row.bytes.length + SEAL_OVERHEAD;
      if (
        part.length &&
        (part.length + 3 > MAX_LOCAL_INDEX_BATCH_RECORDS ||
          bytes + size + INDEX_BLOCK_BYTES > MAX_LOCAL_INDEX_BATCH_BYTES)
      ) {
        yield part;
        part = [];
        bytes = 0;
      }
      part.push(block);
      bytes += size;
    }
    if (part.length) yield part;
  }
  /**
   * Stores blocks that belong to no run yet as the next part of a pending
   * chain; the head names the chain by this part.
   */
  private async commitPending(
    blocks: PendingBlock[],
    prev: string | null,
    signal: AbortSignal
  ) {
    const tail = localIndexRunPartId();
    let added: LocalIndexBlockRef[] = [];
    await this.commit(
      {
        puts: blocks.map(block => block.row),
        deletes: [],
        part: { segmentId: tail, prev, blocks },
        history: (sealedBytes, rowBytes) => {
          added = blocks.map(block => ({
            ...block.ref,
            bytes: rowBytes.get(block.ref.segmentId) ?? 0,
          }));
          return this.history({
            sealedBytes,
            pending: [...this.pending.filter(part => part !== prev), tail],
          });
        },
      },
      signal,
      history => {
        this.pending = history.pending;
      }
    );
    return { tail, blocks: added };
  }
  /**
   * Seals and commits the rows and the head metadata in one write: the head
   * revision and every touched row are compared atomically.
   */
  private async commit(
    step: CommitStep,
    signal: AbortSignal,
    published: (history: LocalIndexHistory) => void
  ) {
    const bytes = step.puts.reduce(
      (sum, row) => sum + row.bytes.length + SEAL_OVERHEAD,
      0
    );
    if (
      step.puts.length + step.deletes.length + (step.part ? 2 : 1) >
        MAX_LOCAL_INDEX_BATCH_RECORDS ||
      bytes > MAX_LOCAL_INDEX_BATCH_BYTES
    )
      throw new IndexBatchTooLarge();
    const store = this.store;
    const expected = this.head;
    const expectedRevision = this.revision;
    const metaId = this.metaId ?? crypto.randomUUID();
    const revision = crypto.randomUUID();
    const meta: LocalIndexSegmentIdentity = {
      segmentId: metaId,
      role: 'metadata',
      table: 'doc',
      recordId: metaId,
      ownerDocId: this.workspaceId,
      nid: 0,
      storageRevision: revision,
    };
    const workspaceScope = docKeyScope(this.workspaceId);
    const done: {
      result?: {
        context: K;
        history: LocalIndexHistory;
        sealedBytes: number;
        metaBytes: number;
      };
    } = {};
    await store.write(async () => {
      const context = await store.prepareWrite(
        expected,
        [
          ...step.puts.map(row => ({
            segmentId: row.identity.segmentId,
            expected: row.expected,
          })),
          ...step.deletes,
        ],
        [
          ...new Set([
            ...step.puts.map(row => docKeyScope(row.identity.ownerDocId)),
            workspaceScope,
          ]),
        ],
        signal
      );
      const sealed = await Promise.all(
        step.puts.map(async ({ identity, bytes }): Promise<LocalIndexRow> => {
          const keyScopeId = docKeyScope(identity.ownerDocId);
          const payload = await context.seal(
            keyScopeId,
            this.scopeOf(identity),
            'index',
            bytes
          );
          return identity.role === 'postings' || identity.role === 'run'
            ? { ...identity, keyScopeId, payload }
            : {
                ...identity,
                keyScopeId,
                payload,
                ...(identity.role === 'record'
                  ? { recordKey: [identity.table, identity.recordId] }
                  : {}),
              };
        })
      );
      const rowBytes = new Map<string, number>();
      let sealedBytes = this.sealedBytes;
      for (const row of step.puts) sealedBytes -= row.replacedBytes;
      for (const row of step.deletes) sealedBytes -= row.bytes;
      for (const row of sealed) {
        rowBytes.set(row.segmentId, cipherBytes(row));
        sealedBytes += cipherBytes(row);
      }
      if (step.part) {
        const part = await this.sealPart(context, step.part, rowBytes);
        sealed.push(part);
        sealedBytes += cipherBytes(part);
      }
      const history = step.history(sealedBytes, rowBytes);
      const plain = encodeLocalIndexHistory(meta, history);
      let metaRow: LocalIndexRow;
      try {
        metaRow = {
          ...meta,
          keyScopeId: workspaceScope,
          payload: await context.seal(
            workspaceScope,
            this.scopeOf(meta),
            'index',
            plain
          ),
        };
      } finally {
        plain.fill(0);
      }
      await store.commitWrite(
        context,
        expected,
        {
          deletes: step.deletes.map(({ segmentId, expected }) => ({
            segmentId,
            expected,
          })),
          puts: [
            // A run part is new: it has no row of step.puts.
            ...sealed.map((row, at) => ({
              row,
              expected: step.puts[at]?.expected ?? null,
            })),
            // The head metadata row always carries the head revision.
            { row: metaRow, expected: expectedRevision },
          ],
          next: { revision, segmentId: metaId },
        },
        signal
      );
      context.releasePreparedKeys();
      done.result = {
        context,
        history,
        sealedBytes,
        metaBytes: cipherBytes(metaRow),
      };
    });
    const committed = done.result;
    if (signal.aborted || !committed) throw localAccountKeyError('LOCKED');
    published(committed.history);
    this.garbage = committed.history.garbage;
    this.sealedBytes = committed.sealedBytes;
    this.metaBytes = committed.metaBytes;
    this.metaId = metaId;
    this.revision = revision;
    this.authorityContext = committed.context;
  }
  /** Seals a run part once the sealed sizes of its blocks are known. */
  private async sealPart(
    context: K,
    part: NonNullable<CommitStep['part']>,
    rowBytes: Map<string, number>
  ): Promise<LocalIndexRow> {
    const identity: LocalIndexBlockIdentity = {
      segmentId: part.segmentId,
      role: 'run',
      ownerDocId: this.workspaceId,
      storageRevision: part.segmentId,
    };
    const plain = encodeLocalIndexRunPart(identity, {
      prev: part.prev,
      blocks: part.blocks.map(block => {
        const bytes = rowBytes.get(block.ref.segmentId);
        if (!bytes) throw localAccountKeyError('CORRUPT');
        return { ...block.ref, bytes };
      }),
    });
    try {
      const keyScopeId = docKeyScope(this.workspaceId);
      return {
        ...identity,
        keyScopeId,
        payload: await context.seal(
          keyScopeId,
          this.scopeOf(identity),
          'index',
          plain
        ),
      };
    } finally {
      plain.fill(0);
    }
  }

  private scheduleMaintenance() {
    if (!this.garbage.length && !this.merge && !this.mergePlan()) return;
    this.maintenance = this.maintenance.then(() => this.maintain());
  }
  /** Merges and deletes replaced blocks, one serialized commit per step. */
  private async maintain() {
    try {
      while (await this.leased(signal => this.maintenanceStep(signal)));
    } catch {
      // The failed step invalidated the index; the next read rebuilds it.
    }
  }
  /** Resolves once no merge or block deletion is left. */
  async settled() {
    let current: Promise<void>;
    do {
      current = this.maintenance;
      await current;
    } while (current !== this.maintenance);
  }
  private async maintenanceStep(signal: AbortSignal): Promise<boolean> {
    if (this.garbage.length) {
      await this.deleteGarbage(signal);
      return true;
    }
    if (!this.merge) {
      const plan = this.mergePlan();
      if (!plan) return false;
      const inputs = this.index.runs.slice(plan.from);
      this.merge = {
        inputs,
        cursors: inputs.map(blocks => ({
          blocks,
          next: 0,
          postings: [],
          at: 0,
        })),
        output: [],
        chain: null,
        deadAtStart: plan.full ? this.deadRecords : null,
      };
    }
    await this.mergeStep(this.merge, signal);
    return true;
  }
  /**
   * Size-tiered runs: the newest runs merge while together they reach half
   * of the run before them, so a run's postings are rewritten O(log N) times.
   * A full merge drops removed records once they exceed 25 % of the live ones.
   */
  private mergePlan(): { from: number; full: boolean } | null {
    const runs = this.index.runs;
    if (!runs.length) return null;
    if (this.deadRecords > 0 && this.deadRecords * 4 > this.index.live.size)
      return { from: 0, full: true };
    let from = runs.length - 1;
    let postings = runPostings(runs[from]);
    while (from > 0 && postings * 2 >= runPostings(runs[from - 1])) {
      from--;
      postings += runPostings(runs[from]);
    }
    if (from < runs.length - 1) return { from, full: false };
    if (runs.length > MAX_INDEX_RUNS)
      return { from: MAX_INDEX_RUNS - 1, full: false };
    return null;
  }
  /** The next live posting of all input runs in key order, or null at the end. */
  private async nextMerged(merge: Merge) {
    for (;;) {
      let best: MergeCursor | null = null;
      for (const cursor of merge.cursors) {
        if (cursor.at >= cursor.postings.length) {
          if (cursor.next >= cursor.blocks.length) continue;
          const ref = cursor.blocks[cursor.next++];
          cursor.postings = (await this.loadBlocks([ref])).get(ref.segmentId)!;
          cursor.at = 0;
        }
        const posting = cursor.postings[cursor.at];
        const head = best?.postings[best.at];
        if (
          !head ||
          posting[1] < head[1] ||
          (posting[1] === head[1] && posting[0] < head[0])
        )
          best = cursor;
      }
      if (!best) return null;
      const posting = best.postings[best.at++];
      if (posting[2] === 0 || this.index.live.has(posting[2])) return posting;
    }
  }
  private async mergeStep(merge: Merge, signal: AbortSignal) {
    const postings: LocalIndexPosting[] = [];
    let bytes = 0;
    let done = false;
    while (bytes < MERGE_STEP_BYTES) {
      const posting = await this.nextMerged(merge);
      if (!posting) {
        done = true;
        break;
      }
      postings.push(posting);
      bytes += postingBytes(posting);
    }
    if (postings.length) {
      const blocks = this.blocks(postings);
      try {
        for (const part of this.commitParts(blocks)) {
          const stored = await this.commitPending(part, merge.chain, signal);
          merge.output.push(...stored.blocks);
          merge.chain = stored.tail;
        }
      } finally {
        for (const block of blocks) block.row.bytes.fill(0);
      }
    }
    if (done) await this.finishMerge(merge, signal);
  }
  /** Switches the input runs to the merged run in one commit. */
  private async finishMerge(merge: Merge, signal: AbortSignal) {
    const from = this.index.runs.indexOf(merge.inputs[0]);
    if (
      from < 0 ||
      merge.inputs.some((run, at) => this.index.runs[from + at] !== run)
    )
      throw localAccountKeyError('CORRUPT');
    const end = from + merge.inputs.length;
    const chain = merge.chain;
    const runs = [
      ...this.index.runs.slice(0, from),
      ...(chain ? [merge.output] : []),
      ...this.index.runs.slice(end),
    ];
    await this.commit(
      {
        puts: [],
        deletes: [],
        history: sealedBytes =>
          this.history({
            sealedBytes,
            runs: [
              ...this.runTails.slice(0, from),
              ...(chain ? [chain] : []),
              ...this.runTails.slice(end),
            ],
            pending: this.pending.filter(part => part !== chain),
            garbage: [...this.garbage, ...this.runTails.slice(from, end)],
            deadRecords:
              merge.deadAtStart === null
                ? this.deadRecords
                : this.deadRecords - merge.deadAtStart,
          }),
      },
      signal,
      history => {
        this.index.runs = runs;
        this.runTails = history.runs;
        this.pending = history.pending;
        this.deadRecords = history.deadRecords;
      }
    );
    this.merge = null;
  }
  /**
   * Deletes the last part of a replaced run or of a merge that did not
   * finish, with its blocks; the chain continues at the part before it.
   */
  private async deleteGarbage(signal: AbortSignal) {
    const tail = this.garbage[0];
    const [part] = await this.loadParts(this.head, [tail], signal);
    await this.commit(
      {
        puts: [],
        deletes: [
          ...part.blocks.map(block => ({
            segmentId: block.segmentId,
            expected: block.segmentId,
            bytes: block.bytes,
          })),
          { segmentId: tail, expected: tail, bytes: part.bytes },
        ],
        history: sealedBytes =>
          this.history({
            sealedBytes,
            garbage: [
              ...(part.prev ? [part.prev] : []),
              ...this.garbage.slice(1),
            ],
          }),
      },
      signal,
      () => {}
    );
  }
  /**
   * Bytes of the sealed rows of this index, counted while writing, once
   * running merges and block deletions end.
   */
  async storedBytes(): Promise<number> {
    await this.settled();
    return await this.read(async () => this.sealedBytes + this.metaBytes);
  }
}
